import React, { useState } from "react";
import {
  Group,
  Burger,
  Avatar,
  Menu,
  Text,
  UnstyledButton,
  Divider,
  Box,
} from "@mantine/core";
import { NavLink, useLocation } from "react-router-dom";
import IconLogout from "@tabler/icons-react/dist/esm/icons/IconLogout.mjs";
import { rolesLabels } from "../../utils/vars";
import { clientBaseUrl } from "../../services/urls";
import { useTranslation } from "react-i18next";
import { clearAccessToken, getTenant, removeTenant } from "../../utils/tenant";
import { logout as keycloakLogout } from "../../auth/keycloak";
import { useUsername } from "../../redux/features/auth/authSlice";
import { useRole } from "../../redux/features/user";
import { useTenancy } from "../../redux/features/permissions/permissionsSlice";
import { ReactComponent as Logo } from "./assets/logo.svg";
import { useNavItems } from "./useNavItems";
import "./Header.scss";

export default function Header({ navbarOpened, onNavbarToggle, compact }) {
  const role = useRole();
  const tenancy = useTenancy();
  const { t } = useTranslation();
  const navItems = useNavItems({ role, tenancy, t });

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">
        {compact && (
          <Burger
            opened={navbarOpened}
            onClick={onNavbarToggle}
            size="sm"
            color="#0AD172"
          />
        )}
        <Logo width={70}>TRM</Logo>
      </Group>

      {/* Center: horizontal nav links — shown while they fit the viewport */}
      {!compact && (
        <Group
          gap={4}
          wrap="nowrap"
          style={{ flex: 1, minWidth: 0, marginLeft: 24 }}
        >
          {navItems.map((item) =>
            item.items ? (
              <NavDropdown key={item.id} item={item} />
            ) : (
              <NavItem key={item.id} item={item} />
            ),
          )}
        </Group>
      )}

      {/* Right: user section (never shrinks, so it can't be pushed off) */}
      <Box style={{ display: "flex", flexShrink: 0 }}>
        <UserSection />
      </Box>
    </Group>
  );
}

/* ─── Single nav link ─────────────────────────────────────────────────────── */
function NavItem({ item }) {
  // used for desktop (to be replaced by design)
  const location = useLocation();
  const isActive =
    item.path === "/"
      ? location.pathname === "/"
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

  return (
    <NavLink
      to={item.path}
      className={isActive ? "trm-nav-link-active" : "trm-nav-link"}
    >
      {item.text}
    </NavLink>
  );
}

/* ─── Dropdown nav item (Reports, Advanced Search) ───────────────────────── */
function NavDropdown({ item }) {
  // used for desktop (to be replaced by design)
  const location = useLocation();
  const isActiveParent = item.items?.some(
    (child) => location.pathname === child.path || location.pathname.startsWith(child.path + "/"),
  );

  return (
    <Menu
      shadow="md"
      width={220}
      trigger="click"
      openDelay={0}
      closeDelay={100}
    >
      <Menu.Target>
        <UnstyledButton
          className={isActiveParent ? "trm-nav-link-active" : "trm-nav-link"}
        >
          {item.text} ▾
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {item.items.map((child) => {
          const isChildActive =
            location.pathname === child.path ||
            location.pathname.startsWith(child.path + "/");
          return (
            <Menu.Item
              key={child.id}
              className={isChildActive ? "trm-dropdown-item-active" : ""}
              p={0}
            >
              <NavLink
                to={child.path}
                className={
                  isChildActive
                    ? "trm-dropdown-link-active"
                    : "trm-dropdown-link"
                }
              >
                {child.text}
              </NavLink>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

/* ─── User section ────────────────────────────────────────────────────────── */
function UserSection() {
  const username = useUsername();
  const role = useRole();
  const { t } = useTranslation();
  const tenant = getTenant();

  function logoutHandler() {
    removeTenant();
    clearAccessToken();
    keycloakLogout({ redirectUri: clientBaseUrl });
  }

  return (
    <Menu shadow="md" width={220} position="bottom-end">
      <Menu.Target>
        <UnstyledButton>
          <Group gap="sm" wrap="nowrap">
            <Box visibleFrom="sm" style={{ textAlign: "right" }}>
              <Text size="sm" c="#0AD172" fw={500} lh={1.2}>
                {username}
              </Text>
              <Text size="xs" c="rgba(10,209,114,0.65)">
                {rolesLabels[role]}
                {tenant?.name ? ` · ${tenant.name}` : ""}
              </Text>
            </Box>
            <Avatar
              color="#0AD172"
              variant="outline"
              radius="xl"
              size="md"
              style={{ border: "2px solid #0AD172", color: "#0AD172" }}
            >
              {username ? username.charAt(0).toUpperCase() : "U"}
            </Avatar>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{username}</Menu.Label>
        <Menu.Label c="dimmed" size="xs">
          {rolesLabels[role]}
        </Menu.Label>
        <Divider my={4} />
        <Menu.Item
          color="red"
          leftSection={<IconLogout size={16} />}
          onClick={logoutHandler}
        >
          {t("header.logout")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/* ─── Navbar nav list (mobile / tablet) ──────────────────────────────────── */
export function NavbarLinks({ onNavigate }) {
  const tenancy = useTenancy();
  const { t } = useTranslation();
  const location = useLocation();
  const navItems = useNavItems({ tenancy, t });

  return (
    <Box p="sm">
      {navItems.map((item) =>
        item.items ? (
          <NavbarDropdown
            key={item.id}
            item={item}
            location={location}
            onNavigate={onNavigate}
          />
        ) : (
          <NavbarLink
            key={item.id}
            item={item}
            location={location}
            onNavigate={onNavigate}
          />
        ),
      )}
    </Box>
  );
}

function NavbarLink({ item, location, onNavigate }) {
  // used for burger menu
  const isActive =
    item.path === "/"
      ? location.pathname === "/"
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

  return (
    <NavLink
      to={item.path}
      className={isActive ? "trm-navbar-link-active" : "trm-navbar-link"}
      onClick={onNavigate}
    >
      {item.text}
    </NavLink>
  );
}

function NavbarDropdown({ item, location, onNavigate }) {
  // used for burger menu
  const [open, setOpen] = useState(false);
  const isActiveParent = item.items?.some(
    (child) => location.pathname === child.path || location.pathname.startsWith(child.path + "/"),
  );

  return (
    <Box mb={4}>
      <UnstyledButton
        onClick={() => setOpen((v) => !v)}
        className={
          isActiveParent ? "trm-navbar-link-active" : "trm-navbar-link"
        }
        style={{ width: "100%" }}
      >
        {item.text} {open ? "▲" : "▾"}
      </UnstyledButton>
      {open && (
        <Box pl="md" mt={2}>
          {item.items.map((child) => (
            <NavbarLink
              key={child.id}
              item={child}
              location={location}
              onNavigate={onNavigate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
